import { Show, type JSX } from 'solid-js';
import { BusySlot } from './busy';
import { testIdOf } from '../testIds';

export interface RangePairDefinition {
  key: string;
  label: string;
  meaning: string;
  min: number;
  max: number;
  step: number;
  /** The package's own suffix for this number, rendered after the readout in its own element. */
  unit?: string;
}

export interface RangePairValues {
  min: number;
  max: number;
}

export type RangePairEdge = 'min' | 'max';

export interface RangePairChange extends RangePairValues {
  edge: RangePairEdge;
  parameters: Record<string, number>;
}

interface RangePairProps {
  minDefinition: RangePairDefinition;
  maxDefinition: RangePairDefinition;
  /** Has the controller answered for this end's last committed move yet? */
  committing?: (key: string) => boolean;
  minValue: number;
  maxValue: number;
  class: string;
  testIdPrefix: string;
  disabled: boolean;
  formatValue: (value: number) => string;
  onChange: (change: RangePairChange, final: boolean) => void;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

/**
 * Move exactly one edge of a range pair and include both values in the resulting patch.
 * The other edge is deliberately copied unchanged: it is a boundary, never a value to reconcile.
 */
export function rangePairChange(
  minDefinition: RangePairDefinition,
  maxDefinition: RangePairDefinition,
  current: RangePairValues,
  edge: RangePairEdge,
  requested: number,
): RangePairChange {
  if (!Number.isFinite(requested)) {
    return {
      edge,
      ...current,
      parameters: {
        [minDefinition.key]: current.min,
        [maxDefinition.key]: current.max,
      },
    };
  }

  const next = edge === 'min'
    ? {
        min: Math.min(clamp(requested, minDefinition.min, minDefinition.max), current.max),
        max: current.max,
      }
    : {
        min: current.min,
        max: Math.max(clamp(requested, maxDefinition.min, maxDefinition.max), current.min),
      };

  return {
    edge,
    ...next,
    parameters: {
      [minDefinition.key]: next.min,
      [maxDefinition.key]: next.max,
    },
  };
}

export function RangePair(props: RangePairProps): JSX.Element {
  const current = (): RangePairValues => ({ min: props.minValue, max: props.maxValue });
  const emit = (input: HTMLInputElement, edge: RangePairEdge, final: boolean): void => {
    const change = rangePairChange(
      props.minDefinition,
      props.maxDefinition,
      current(),
      edge,
      input.valueAsNumber,
    );
    input.value = String(change[edge]);
    props.onChange(change, final);
  };

  return (
    <>
      <label class={props.class}>
        <span><strong>{props.minDefinition.label}</strong><small>{props.minDefinition.meaning}</small></span>
        <output data-testid={testIdOf('controlValue', { prefix: props.testIdPrefix, key: props.minDefinition.key })}>
          <BusySlot busy={props.committing?.(props.minDefinition.key) === true} />
          {props.formatValue(props.minValue)}
          <Show when={props.minDefinition.unit}><span class="parameter-unit">{props.minDefinition.unit}</span></Show>
        </output>
        <input
          type="range"
          min={props.minDefinition.min}
          max={props.minDefinition.max}
          step={props.minDefinition.step}
          value={props.minValue}
          disabled={props.disabled}
          aria-label={props.minDefinition.label}
          data-testid={testIdOf('control', { prefix: props.testIdPrefix, key: props.minDefinition.key })}
          data-range-pair-edge="min"
          onInput={(event) => emit(event.currentTarget, 'min', false)}
          onChange={(event) => emit(event.currentTarget, 'min', true)}
        />
      </label>

      <label class={props.class}>
        <span><strong>{props.maxDefinition.label}</strong><small>{props.maxDefinition.meaning}</small></span>
        <output data-testid={testIdOf('controlValue', { prefix: props.testIdPrefix, key: props.maxDefinition.key })}>
          <BusySlot busy={props.committing?.(props.maxDefinition.key) === true} />
          {props.formatValue(props.maxValue)}
          <Show when={props.maxDefinition.unit}><span class="parameter-unit">{props.maxDefinition.unit}</span></Show>
        </output>
        <input
          type="range"
          min={props.maxDefinition.min}
          max={props.maxDefinition.max}
          step={props.maxDefinition.step}
          value={props.maxValue}
          disabled={props.disabled}
          aria-label={props.maxDefinition.label}
          data-testid={testIdOf('control', { prefix: props.testIdPrefix, key: props.maxDefinition.key })}
          data-range-pair-edge="max"
          onInput={(event) => emit(event.currentTarget, 'max', false)}
          onChange={(event) => emit(event.currentTarget, 'max', true)}
        />
      </label>
    </>
  );
}
